/**
 * Wave 29 Chunk 9a — Team Competitions list page.
 *
 * Full-page layout (DESIGN_CONTRACT Rule 0). Shows active + closed
 * competitions, with a "+ New competition" button routing to
 * /team/competitions/new and rows linking to /team/competitions/:id.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Plus, Loader2, Filter, Clock, Sparkles, ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const STATUSES = [
  { v: "active", l: "Active" },
  { v: "closed", l: "Closed" },
  { v: "all", l: "All" },
];

export default function TeamCompetitions() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [status, setStatus] = useState("active");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = status === "all" ? {} : { status };
      const { data } = await api.get("/team/competitions", { params });
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load competitions.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, status]);

  return (
    <div className="space-y-5 max-w-5xl" data-testid="team-competitions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Trophy className="w-6 h-6 text-amber-300" /> Competitions
        </h1>
        {isHr ? (
          <Link
            to="/team/competitions/new"
            className="hv-btn hv-btn-primary text-xs"
            data-testid="comp-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New competition
          </Link>
        ) : null}
      </div>

      <div className="hv-card p-3 flex items-center gap-2 flex-wrap" data-testid="comp-filters">
        <Filter className="w-3.5 h-3.5 text-[#647087]" />
        {STATUSES.map((s) => (
          <button
            key={s.v}
            onClick={() => setStatus(s.v)}
            data-testid={`comp-filter-${s.v}`}
            className={
              "px-2.5 py-1 rounded-md text-[11px] " +
              (status === s.v
                ? "bg-[#1FB6C1] text-black font-semibold"
                : "bg-white/5 text-[#dde3ec] hover:bg-white/10")
            }
          >
            {s.l}
          </button>
        ))}
      </div>

      {busy ? (
        <div className="hv-card p-6 flex items-center gap-2 text-[#9aa3b5] text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="hv-card p-6 text-sm text-[#9aa3b5] text-center" data-testid="comp-empty">
          <Sparkles className="w-5 h-5 mx-auto mb-2 text-[#647087]" />
          No competitions yet.{" "}
          {isHr && (
            <Link className="text-[#1FB6C1] hover:underline" to="/team/competitions/new">
              Create the first one
            </Link>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((c) => (
            <li key={c.id} className="hv-card p-4" data-testid={`comp-row-${c.id}`}>
              <Link
                to={`/team/competitions/${c.id}`}
                className="flex items-center justify-between gap-3 group"
                data-testid={`comp-link-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white truncate">{c.name}</span>
                    <span
                      className={
                        "px-1.5 py-0.5 rounded text-[10px] font-bold " +
                        (c.status === "active"
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-zinc-500/20 text-zinc-300")
                      }
                    >
                      {c.status}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-[#cbd5e1]">
                      {c.metric_source === "auto" ? `auto · ${c.metric_key}` : "manual scoring"}
                    </span>
                    {c.auto_grant_points && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-300">
                        auto points
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#9aa3b5] mt-1 flex items-center gap-2 flex-wrap">
                    <Clock className="w-3 h-3" /> {c.start_date} → {c.end_date}
                    {c.prize ? <span className="text-amber-300">· {c.prize}</span> : null}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#647087] group-hover:text-white shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
